import React from 'react';
import { motion } from 'framer-motion';
import { 
  Server, 
  Shield, 
  Lock, 
  Cloud,
  Code,
  Database,
  Network,
  Cpu,
  CheckCircle,
  ArrowRight,
  AlertTriangle,
  Key
} from 'lucide-react';

interface TechnologyProps {
  setCurrentPage: (page: string) => void;
}

const Technology: React.FC<TechnologyProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: Shield,
      title: "DevSecOps Integration",
      description: "Seamless security integration throughout the development lifecycle.",
      items: [
        "Security automation",
        "CI/CD pipeline security",
        "Code security scanning",
        "Container security"
      ]
    },
    {
      icon: Lock,
      title: "IP Protection",
      description: "Advanced measures to protect intellectual property and proprietary technology.",
      items: [
        "Source code protection",
        "Patent security",
        "Trade secret safeguards",
        "Access control systems"
      ]
    },
    {
      icon: Cloud,
      title: "Cloud Security",
      description: "Comprehensive security for cloud infrastructure and applications.",
      items: [
        "Cloud infrastructure security",
        "Containerization security",
        "Microservices protection",
        "Serverless security"
      ]
    },
    {
      icon: Network,
      title: "API Security",
      description: "Robust protection for APIs and service integrations.",
      items: [
        "API authentication",
        "Rate limiting",
        "Input validation",
        "Security monitoring"
      ]
    }
  ];

  const solutions = [
    {
      icon: Code,
      title: "Application Security",
      items: [
        "SAST/DAST testing",
        "Dependency scanning",
        "Vulnerability management",
        "Security testing"
      ]
    },
    {
      icon: Database,
      title: "Data Protection",
      items: [
        "Data encryption",
        "Access controls",
        "Data governance",
        "Privacy compliance"
      ]
    },
    {
      icon: Key,
      title: "Identity Security",
      items: [
        "Identity management",
        "Access control",
        "SSO integration",
        "Authentication systems"
      ]
    }
  ];

  const capabilities = [
    {
      icon: Cpu,
      title: "Infrastructure Security",
      description: "Advanced security for technology infrastructure and systems.",
      features: [
        "Network security",
        "System hardening",
        "Configuration management",
        "Security monitoring"
      ]
    },
    {
      icon: Shield,
      title: "Threat Protection",
      description: "Comprehensive threat detection and prevention for tech environments.",
      features: [
        "Threat intelligence",
        "Attack prevention",
        "Security analytics",
        "Incident response"
      ]
    },
    {
      icon: AlertTriangle,
      title: "Risk Management",
      description: "Proactive risk assessment and management for technology operations.",
      features: [
        "Risk assessment",
        "Compliance monitoring",
        "Security audits",
        "Policy enforcement"
      ]
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <Server className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Technology Security Solutions</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Advanced cybersecurity solutions designed specifically for technology companies, protecting your innovation, infrastructure, and digital assets
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold ml-4">{feature.title}</h3>
              </div>
              
              <p className="text-muted mb-6">{feature.description}</p>
              
              <ul className="space-y-3">
                {feature.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                    <span className="text-muted">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Solutions Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Security Solutions</h2>
            <p className="text-lg text-muted">
              Comprehensive security solutions for modern technology companies
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <solution.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{solution.title}</h3>
                </div>
                
                <ul className="space-y-3">
                  {solution.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Capabilities Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Advanced Capabilities</h2>
            <p className="text-lg text-muted">
              State-of-the-art security capabilities for technology environments
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <capability.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{capability.title}</h3>
                </div>
                
                <p className="text-muted mb-6">{capability.description}</p>

                <ul className="space-y-3">
                  {capability.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Secure Your Technology Company</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Contact our technology security experts to discuss your company's specific needs and discover how we can help protect your innovation
            </p>
            <button 
              onClick={() => setCurrentPage('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Technology;